import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionPricingMonthly from "../../sections/pricing/section-pricing-monthly";
import SectionPricingAnnual from "../../sections/pricing/section-pricing-annual";
import axiosInstance from "../../../../../axiosInstance";
import { API_URL } from "../../../../../config";
import { toast } from "react-toastify";
import { Helmet } from 'react-helmet-async';

const styles = {
    wrapper: {
        backgroundColor: '#E53935',
        padding: '40px',
        borderRadius: '10px',
        color: 'white',
        maxWidth: '100%',
        fontFamily: 'sans-serif',
    },
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '40px',
    },
    left: {
        flex: '1',
        minWidth: '220px',
    },
    center: {
        minWidth: '320px',
    },
    right: {
        flex: '1',
        minWidth: '220px',
    },
    badge: {
        backgroundColor: '#ffcccb',
        color: '#b71c1c',
        padding: '6px 12px',
        borderRadius: '20px',
        fontWeight: 'bold',
        display: 'inline-block',
        marginBottom: '10px',
    },
    title: {
        fontSize: '32px',
        marginBottom: '8px',
    },
    subBadge: {
        border: '1px solid white',
        borderRadius: '20px',
        padding: '5px 15px',
        display: 'inline-block',
        marginBottom: '20px',
    },
    priceBox: {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        color: 'black',
        maxWidth: '400px',
    },
    saveText: {
        backgroundColor: '#f8d7da',
        color: '#c62828',
        fontWeight: 'bold',
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '10px',
        marginBottom: '10px',
    },
    price: {
        fontSize: '22px',
        margin: '10px 0',
    },
    oldPrice: {
        color: 'gray',
    },
    newPrice: {
        fontWeight: 'bold',
        color: 'green',
    },
    afterpay: {
        fontSize: '14px',
        color: '#444',
    },
    savings: {
        fontSize: '14px',
        marginTop: '8px',
    },
    highlight: {
        color: '#E53935',
        fontWeight: 'bold',
    },
    button: {
        marginTop: '15px',
        backgroundColor: '#E53935',
        color: 'white',
        padding: '10px 20px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 'bold',
        width: '100%',
    },
    secured: {
        fontSize: '12px',
        textAlign: 'center',
        marginTop: '10px',
        color: '#555',
    },
    feature: {
        marginBottom: '10px',
        fontSize: '16px',
    }
}

function PricingPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const response = await axiosInstance.get(`${API_URL}/admin/getAll_Subscription`);
                setSubscriptions(response.data.data);
            } catch (error) {
                console.error("Failed to fetch subscriptions", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSubscriptions();
    }, []);

    const handleClick = async (plan) => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            toast.error("Please login to continue.");
            navigate("/login");
            return;
        }
        try {
            const response = await axiosInstance.post(`${API_URL}/generate-testurl`, {
                subscriptionType: plan.subscriptionType || "lifetime",
                planName: plan.title || "Premium Plan",
                price: Math.round(Number(plan.price) * 100),
                currency: 'CAD',
                subscriptionId: plan.id,
                RequestId: '',
                status: 'active'
            });

            if (response.data?.url) {
                window.open(response.data.url, '_blank');
            } else {
                toast.error("Failed to redirect to payment.");
            }
        } catch (err) {
            console.error("Checkout error:", err.response?.data || err.message);
            toast.error("Error starting checkout session.");
        }
    };

    if (loading) return <div>{t("loading")}</div>;

    return (
        <>
            <Helmet>
                <title>Pricing | Job Contact</title>
                <meta name="description" content="Welcome to the home page of MyApp." />
            </Helmet>
            <div className="p-t120 p-b90 site-bg-white tw-pricing-area">
                <div className="container">
                    <div className="section-head left wt-small-separator-outer">
                        <h2 className="wt-title">{t("One single payment, no subscription required to find your dream job.")}</h2>
                    </div>
                    {subscriptions.map((plan, index) => (
                        <div key={index} style={styles.wrapper}>
                            <div style={styles.container}>
                                {/* Left */}
                                <div style={styles.left}>
                                    <div style={styles.badge}>⭐ {t("Limited Time Available")}</div><br /><br />
                                    {/* <h1 style={styles.title}>∞ {t("Lifetime Access")}</h1> */}
                                    <div style={styles.subBadge}>{t("One-time payment")}</div>
                                </div>
                                {/* Center (Price Box) */}
                                <div style={styles.center}>
                                    <div style={styles.priceBox}>
                                        <div style={styles.saveText}>{t("Save 50% now")}</div>
                                        <div style={styles.price}>
                                            <span style={styles.newPrice}> {plan.price} $ CAD</span>
                                        </div>
                                        <button
                                            style={styles.button}
                                            onClick={() => handleClick(plan)}
                                        >
                                            {t("Get Started")} →
                                        </button>
                                        <p style={styles.secured}>🔒 Secured by <b>Stripe</b></p>
                                    </div>
                                </div>

                                {/* Right (Features) */}
                                <div style={styles.right}>
                                    <p style={styles.feature}>✔ {t("Several applications per week")}</p>
                                    <p style={styles.feature}>✔ {t("Displaying your profile on the site")}</p>
                                    <p style={styles.feature}>✔ {t("Unlimited job match")}</p>
                                    <p style={styles.feature}>✔ {t("Manual job search")}</p>
                                    <p style={styles.feature}>✔ {t("Email Support")}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default PricingPage;


// import { useEffect, useState } from "react";
// import { useTranslation } from "react-i18next";
// import SectionPricingMonthly from "../../sections/pricing/section-pricing-monthly";
// import SectionPricingAnnual from "../../sections/pricing/section-pricing-annual";
// import axiosInstance from "../../../../../axiosInstance";
// import { API_URL } from "../../../../../config";

// function PricingPage() {
//     const { t, i18n } = useTranslation();
//     const [subscriptions, setSubscriptions] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const changeLanguage = (lng) => {
//         console.log(lng);
//         i18n.changeLanguage(lng);
//       };
//     useEffect(() => {
//         const fetchSubscriptions = async () => {
//             try {
//                 const response = await axiosInstance.get(`${API_URL}/admin/getAll_Subscription`);
//                 setSubscriptions(response.data.data);
//             } catch (error) {
//                 console.error("Failed to fetch subscriptions", error);
//             } finally {
//                 setLoading(false);
//             }
//         };

//         fetchSubscriptions();
//     }, []);

//     if (loading) return <div>{t("loading")}</div>;

//     return (
//         <div className="section-full p-t120 p-b90 site-bg-white tw-pricing-area">
//             <div className="container">
//                 {/* Language Switch Buttons */}
//                 <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "20px" }}>
//                 <button onClick={() => changeLanguage('en')} className="nav-link active" id="Monthly" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab">
//                                     {t("monthly")}
//                                 </button>
//                     <button onClick={() => changeLanguage('en')}>EN</button>
//                     <button onClick={() => changeLanguage('hi')}>हिन्दी</button>
//                 </div>

//                 <div className="section-head left wt-small-separator-outer">
//                     <div className="wt-small-separator site-text-primary">
//                         <div>{t("choosePlan")}</div>
//                     </div>
//                     <h2 className="wt-title">{t("saveDiscount")}</h2>
//                 </div>

//                 <div className="section-content">
//                     <div className="twm-tabs-style-1">
//                         <ul className="nav nav-tabs" id="myTab3" role="tablist">
//                             <li className="nav-item" role="presentation">
//                                 <button className="nav-link active" id="Monthly" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab">
//                                     {t("monthly")}
//                                 </button>
//                             </li>
//                             <li className="nav-item" role="presentation">
//                                 <button className="nav-link" id="annual" data-bs-toggle="tab" data-bs-target="#profile" type="button" role="tab" aria-controls="profile">
//                                     {t("annual")}
//                                 </button>
//                             </li>
//                         </ul>
//                         <div className="tab-content" id="myTab3Content">
//                             <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="Monthly">
//                                 <SectionPricingMonthly subscriptions={subscriptions} />
//                             </div>
//                             <div className="tab-pane fade" id="profile" role="tabpanel" aria-labelledby="annual">
//                                 <SectionPricingAnnual subscriptions={subscriptions} />
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }

// export default PricingPage;
